import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { PageHeader } from '@/lib/page-header'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react'
import { format } from 'date-fns'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { usePrimoData, getPrice, formatEUR, PRICE_LEVELS } from '@/lib/primodata'
import { usePriceFilters, useFilteredData } from '@/lib/price-filters'

export const Route = createFileRoute('/_shell/aendringer')({
  component: AendringerPage,
})

function StatusBadge({ status }: { status: string }) {
  const cfg: Record<string, { label: string; className: string }> = {
    'Price change': {
      label: 'Prisændring',
      className: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    },
    'New pack': {
      label: 'Ny pakke',
      className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    },
    Discontinued: {
      label: 'Udgået',
      className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    },
  }
  const { label, className } = cfg[status] ?? { label: status, className: 'bg-muted text-muted-foreground' }
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {label}
    </span>
  )
}

interface ChangeRow {
  date: string | null
  drug: string
  pkg: string
  country: string
  price: number | null
  localPrice: number | null
  localCurrency: string
  status: string
  cot: number | null
}

function AendringerPage() {
  const { data, isLoading } = usePrimoData()
  const allFiltered = useFilteredData(data)
  const { priceLevel } = usePriceFilters()
  const priceLevelLabel = PRICE_LEVELS.find(l => l.value === priceLevel)?.label ?? priceLevel

  const [sorting, setSorting] = useState<SortingState>([{ id: 'date', desc: true }])
  const [globalFilter, setGlobalFilter] = useState('')

  const changesData = useMemo<ChangeRow[]>(() =>
    allFiltered
      .filter(r => r.Status !== 'No change')
      .map(r => ({
        date: r['Price Start Date'],
        drug: r['Global Product Name'],
        pkg: r.Packages,
        country: r.Country,
        price: getPrice(r, priceLevel),
        localPrice: r['Local Price'],
        localCurrency: r['Local Currency'] ?? '',
        status: r.Status,
        cot: r.COT,
      })),
    [allFiltered, priceLevel]
  )

  const columns = useMemo<ColumnDef<ChangeRow>[]>(() => [
    {
      accessorKey: 'date',
      header: ({ column }) => (
        <Button variant="ghost" size="sm" onClick={() => column.toggleSorting()} className="h-7 gap-1 -ml-2 font-medium">
          Dato
          {column.getIsSorted() === 'asc' ? <ArrowUp className="h-3 w-3" /> :
           column.getIsSorted() === 'desc' ? <ArrowDown className="h-3 w-3" /> :
           <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
        </Button>
      ),
      cell: ({ getValue }) => {
        const v = getValue() as string | null
        return v ? (
          <span className="font-mono text-sm">{format(new Date(v), 'dd.MM.yyyy')}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
    {
      accessorKey: 'status',
      header: 'Type',
      cell: ({ getValue }) => <StatusBadge status={getValue() as string} />,
    },
    {
      accessorKey: 'drug',
      header: ({ column }) => (
        <Button variant="ghost" size="sm" onClick={() => column.toggleSorting()} className="h-7 gap-1 -ml-2 font-medium">
          Lægemiddel
          {column.getIsSorted() === 'asc' ? <ArrowUp className="h-3 w-3" /> :
           column.getIsSorted() === 'desc' ? <ArrowDown className="h-3 w-3" /> :
           <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
        </Button>
      ),
    },
    {
      accessorKey: 'pkg',
      header: 'Pakke',
      cell: ({ getValue }) => (
        <span className="text-xs text-muted-foreground max-w-[220px] block truncate" title={getValue() as string}>
          {getValue() as string}
        </span>
      ),
    },
    {
      accessorKey: 'country',
      header: ({ column }) => (
        <Button variant="ghost" size="sm" onClick={() => column.toggleSorting()} className="h-7 gap-1 -ml-2 font-medium">
          Land
          {column.getIsSorted() === 'asc' ? <ArrowUp className="h-3 w-3" /> :
           column.getIsSorted() === 'desc' ? <ArrowDown className="h-3 w-3" /> :
           <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
        </Button>
      ),
    },
    {
      accessorKey: 'price',
      header: ({ column }) => (
        <Button variant="ghost" size="sm" onClick={() => column.toggleSorting()} className="h-7 gap-1 -ml-2 font-medium">
          {priceLevelLabel}
          {column.getIsSorted() === 'asc' ? <ArrowUp className="h-3 w-3" /> :
           column.getIsSorted() === 'desc' ? <ArrowDown className="h-3 w-3" /> :
           <ArrowUpDown className="h-3 w-3 text-muted-foreground" />}
        </Button>
      ),
      cell: ({ getValue }) => (
        <span className="font-mono text-right block">{formatEUR(getValue() as number | null)}</span>
      ),
    },
    {
      accessorKey: 'localPrice',
      header: 'Lokal pris',
      cell: ({ row }) => {
        const lp = row.original.localPrice
        const lc = row.original.localCurrency
        if (!lp || !lc) return <span className="text-muted-foreground">—</span>
        return (
          <span className="font-mono text-right block text-xs">
            {new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(lp)} {lc}
          </span>
        )
      },
    },
    {
      accessorKey: 'cot',
      header: 'Behandlingsomk.',
      cell: ({ getValue }) => {
        const v = getValue() as number | null
        return v ? (
          <span className="font-mono text-right block text-xs">{formatEUR(v)}</span>
        ) : (
          <span className="text-muted-foreground">—</span>
        )
      },
    },
  ], [priceLevelLabel])

  const table = useReactTable({
    data: changesData,
    columns,
    state: { sorting, globalFilter },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: { pagination: { pageSize: 30 } },
  })

  return (
    <div className="p-6 space-y-6">
      <PageHeader title="Prisændringer" subtitle="Pakker med ændringer sorteret efter dato" />

      {isLoading ? (
        <p className="text-muted-foreground">Indlæser data…</p>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <Input
              placeholder="Søg…"
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
              className="h-9 max-w-xs text-sm"
            />
            <div className="flex gap-3 ml-auto text-xs text-muted-foreground">
              <span>
                <span className="inline-block h-2 w-2 rounded-full bg-amber-500 mr-1 relative top-[1px]" />
                {changesData.filter(r => r.status === 'Price change').length} prisændringer
              </span>
              <span>
                <span className="inline-block h-2 w-2 rounded-full bg-green-500 mr-1 relative top-[1px]" />
                {changesData.filter(r => r.status === 'New pack').length} nye pakker
              </span>
              <span>
                <span className="inline-block h-2 w-2 rounded-full bg-red-500 mr-1 relative top-[1px]" />
                {changesData.filter(r => r.status === 'Discontinued').length} udgåede
              </span>
            </div>
          </div>

          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map(hg => (
                  <TableRow key={hg.id} className="bg-muted/50 hover:bg-muted/50">
                    {hg.headers.map(h => (
                      <TableHead key={h.id} className="py-2">
                        {flexRender(h.column.columnDef.header, h.getContext())}
                      </TableHead>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows.map(row => (
                  <TableRow key={row.id} className="hover:bg-muted/30">
                    {row.getVisibleCells().map(cell => (
                      <TableCell key={cell.id} className="py-2 text-sm">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                {table.getRowModel().rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} className="py-12 text-center text-muted-foreground">
                      Ingen ændringer fundet for valgte filtre.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-2">
              <Button
                variant="outline" size="sm"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
              >
                Forrige
              </Button>
              <Button
                variant="outline" size="sm"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
              >
                Næste
              </Button>
            </div>
            <span className="text-xs text-muted-foreground">
              {table.getFilteredRowModel().rows.length} ændringer ·{' '}
              side {table.getState().pagination.pageIndex + 1} / {table.getPageCount()}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
